import * as THREE from 'three';
import type { BallState } from './BallState';
import type { SurfaceSample } from './TerrainSurfaceProbe';

export interface CollisionConfig {
  readonly radiusMeters: number;
  readonly restSpeedThreshold: number;
}

export const DEFAULT_COLLISION: CollisionConfig = {
  radiusMeters: 0.02135,
  restSpeedThreshold: 0.08,
};

/** Resolves terrain contact without depending on terrain mesh internals. */
export class CollisionResolver {
  constructor(private readonly config: CollisionConfig = DEFAULT_COLLISION) {}

  resolve(state: BallState, sample: SurfaceSample): boolean {
    const contactY = sample.height + this.config.radiusMeters;
    state.altitude = state.position.y - contactY;
    state.surfaceType = sample.properties.type;

    if (state.position.y >= contactY) return false;

    state.position.y = contactY;
    state.altitude = 0;
    state.bounceCount += 1;

    const normalVelocity = state.velocity.dot(sample.normal);
    if (normalVelocity < 0) {
      const bounceDamping = Math.pow(0.62, Math.max(0, state.bounceCount - 1));
      const restitution =
        sample.properties.restitution * state.bounceRestitutionMultiplier * bounceDamping;
      state.velocity.addScaledVector(sample.normal, -(1 + restitution) * normalVelocity);
    }

    const updatedNormalVelocity = sample.normal
      .clone()
      .multiplyScalar(state.velocity.dot(sample.normal));
    const tangentVelocity = state.velocity.clone().sub(updatedNormalVelocity);
    const spinStopping = Math.min(0.45, state.spin.length() * sample.properties.spinGrab);
    const retention = Math.max(0.04, sample.properties.landingTangentialRetention - spinStopping);
    const bounceRetention =
      state.bounceCount === 1 ? retention : THREE.MathUtils.lerp(retention, 0.72, 0.35);
    tangentVelocity.multiplyScalar(bounceRetention);
    state.velocity.copy(tangentVelocity.add(updatedNormalVelocity));
    state.spin.multiplyScalar(Math.max(0.15, 1 - sample.properties.friction * 0.45));
    state.phase = state.velocity.dot(sample.normal) > 0.35 ? 'bouncing' : 'rolling';

    return true;
  }
}
